import React from 'react';
import Tooltip from 'rc-tooltip';
import { isTrue, getViceVersaFullName } from '../../../../utilities/string';
import { FormattedMessage, FormattedRelative } from 'react-intl';
import messages from '../../../../text/messages';
import { dateToFloatingString } from '../../../../utilities/dateTime';
import ExtendButton from '../../../../components/richButtons/extendButton';
import Circles from './circles';
import { Snackbar } from '../../../../components/snackbar';
import { history } from '../../../../configuration/store';
import { workerLink } from './_common';

export const Builder = {
    name: (worker, size, onClick = () => null) => {
        const { user } = worker;
        return (
            <div className={size} onClick={onClick}>
                <i className="material-icons hidden-xs">account_circle</i>
                <span>{getViceVersaFullName(user)}</span>
            </div>
        );
    },

    nameWithResend: (worker, size, resendJobInvite, onClick = () => null) => {
        const { user } = worker;
        return (
            <div className={size} onClick={onClick}>
                <div className="name-column">
                    <div className="name-clumn-value">
                        <i className="material-icons hidden-xs">account_circle</i>
                        <span>{getViceVersaFullName(user)}</span>
                    </div>
                    <div>{resendButton(worker, resendJobInvite)}</div>
                </div>
            </div>
        );
    },

    status: (worker, size) => {
        const { user } = worker;
        const userStatus = user.status;
        const jobStatus = worker.status;
        let displayStatus = '-';

        if (jobStatus === 'active') {
            displayStatus = 'Complete';
        } else if (jobStatus === 'submitted') {
            displayStatus = 'In Review';
        } else if (userStatus === 'active') {
            displayStatus = 'Logged In';
        } else if (userStatus === 'pending') {
            displayStatus = 'Invited';
        }
        return (
            <div className={size}>
                <span className="text-capitalize">{displayStatus}</span>
            </div>
        );
    },

    extend: (worker, tenant, size, projectsOptions = [], users = [], inviteUser = () => null) => {
        return (
            <div className={size}>
                {tenant === 'client' ? (
                    <ExtendButton worker={worker} projectsOptions={projectsOptions} users={users} inviteUser={inviteUser} isExtend={true} />
                ) : (
                    <span className="button" onClick={() => workerLink(worker, history, 1)}>
                        <FormattedMessage {...messages.extend} />
                    </span>
                )}
            </div>
        );
    },

    classification: (worker, size) => {
        const { classify_result } = worker;
        const className = `worker-classification-${classify_result}`;
        const centeredSize = `${size} col-centered`;
        return (
            <div className={centeredSize}>
                <span className={className}>{classify_result}</span>
            </div>
        );
    },

    title: (worker, size, onClick = () => null) => {
        const { title } = worker;
        return (
            <div className={`${size} dashboard-title-column`} onClick={onClick}>
                <span className="text-capitalize">{title}</span>
            </div>
        );
    },

    client: (worker, size, onClick = () => null) => {
        const { client_name } = worker;
        return (
            <div className={`${size} dashboard-title-column`} onClick={onClick}>
                <span className="text-capitalize">{client_name}</span>
            </div>
        );
    },

    inviteDate: (worker, size) => {
        const { created_at } = worker;
        let date = {
            value: created_at,
            style: 'numeric'
        };

        return (
            <div className={size}>
                <span>
                    <FormattedRelative {...date} />
                </span>
            </div>
        );
    },

    endDate: (worker, size, onClick = () => null) => {
        const { end_date } = worker;

        return (
            <div className={size} onClick={onClick}>
                {end_date ? <span>{dateToFloatingString(end_date, 'MMM D')}</span> : <FormattedMessage {...messages.ongoing} tagName={'span'} />}
            </div>
        );
    },

    startDate: (worker, size, onClick = () => null) => {
        const { start_date } = worker;

        return (
            <div className={size} onClick={onClick}>
                <span>{dateToFloatingString(start_date, 'MMM D')}</span>
            </div>
        );
    },

    circle: (worker, size) => {
        const circles = Circles.build(worker);
        return (
            <div className={`${size} circles`}>
                {circles.map(
                    ({ color, full, text }, i) =>
                        text ? (
                            <Tooltip
                                key={i}
                                trigger={'hover'}
                                placement={'top'}
                                overlay={text ? <FormattedMessage {...text} /> : null}
                                overlayClassName={'help-button-tooltip'}
                            >
                                <div className={`circle ${color} ${full ? 'full' : 'open'}`} />
                            </Tooltip>
                        ) : (
                            <div key={i} className={`circle ${color} ${full ? 'full' : 'open'}`} />
                        )
                )}
            </div>
        );
    },

    circleBasedStatus: (worker, size) => {
        const circles = Circles.build(worker);
        const lastCircle = circles.reverse().find(c => c.color !== 'empty' && !!c.text);
        const text = lastCircle ? lastCircle.text : messages.emptyMessage;

        return (
            <div className={`${size}`}>
                <span>
                    <FormattedMessage {...text} />
                </span>
            </div>
        );
    },

    contractProgress: (worker, size) => {
        const { po_value, total_billed } = worker;

        if (!po_value) {
            return (
                <div className={size}>
                    <FormattedMessage {...messages.noPo} tagName={'span'} />
                </div>
            );
        }

        const contractProgress = po_value && Math.round(po_value && 100.0 * (total_billed || 0) / po_value);
        let color = contractProgress && contractProgress >= 80 ? 'high' : 'low';

        return (
            <div className={size}>
                <div className="row">
                    <div className="col-sm-9 hidden-xs contract-progress-wrapper">
                        <div className="contract-progress">
                            <div className={`contract-progress-value ${color}`} style={{ width: `${contractProgress}%` }} />
                        </div>
                    </div>
                    <div className="hidden-xs contract-progress-percentage">
                        <span>{contractProgress}%</span>
                    </div>
                    <div className="visible-xs col-xs-12">
                        <span>{contractProgress}%</span>
                    </div>
                </div>
            </div>
        );
    }
};

const resendButton = (worker, resendJobInvite) => {
    const clickHanle = e => {
        e.stopPropagation();
        resendJobInvite(worker, () => Snackbar.success(<FormattedMessage {...messages.inviteResent} />));
    };
    const {
        onboarding: { login }
    } = worker;
    return (
        !isTrue(login) && (
            <div className="resendButton">
                <i className="material-icons" onClick={clickHanle}>
                    autorenew
                </i>
            </div>
        )
    );
};



// WEBPACK FOOTER //
// ./src/pages/dashboard/components/workers/columnBuilder.jsx